import { getApiUrl } from './apiConfig';
import { bytesFromBase64, UPLOAD_BINARY_MAX_BYTES, videoTooLargeMessage } from './videoUploadLimits';

const YOUTUBE_RECONNECT_HINT =
  'YouTube upload access expired. Tap Reconnect YouTube where shown, then try again.';

export const YOUTUBE_UPLOAD_LIMIT_MESSAGE =
  'YouTube daily upload limit reached for this channel. Wait until the limit resets (often within 24 hours), or paste an existing YouTube link instead of uploading a new file.';

/**
 * Turn upload-binary API JSON into a user-visible message (matches Course Editor detail order).
 * @param {Record<string, unknown>} data
 * @param {string} [fallback]
 * @param {number} [httpStatus]
 */
export function isYoutubeReconnectError(message) {
  return /invalid_grant|expired or revoked|reconnect youtube/i.test(String(message || ''));
}

export function isYoutubeUploadLimitError(message) {
  return (
    /uploadLimitExceeded|exceeded the number of videos they may upload|daily upload limit reached/i.test(
      String(message || '')
    )
  );
}

export function formatYoutubeUploadError(data, fallback = 'YouTube upload failed', httpStatus) {
  if (httpStatus === 413) {
    const fromApi = data && typeof data === 'object' ? String(data.message || data.error || '').trim() : '';
    return fromApi || `Video file is too large (max about ${Math.round(UPLOAD_BINARY_MAX_BYTES / (1024 * 1024))} MB). Trim or compress it, or paste a YouTube link instead.`;
  }
  if (httpStatus === 502 || httpStatus === 504) {
    return 'The server timed out. If this keeps happening, refresh the page and try again — large videos should upload directly to YouTube.';
  }
  if (!data || typeof data !== 'object') {
    if (httpStatus && httpStatus >= 500) {
      return `Server error during YouTube upload (HTTP ${httpStatus}). Try a smaller video or check again later.`;
    }
    if (httpStatus && httpStatus >= 400) {
      return `YouTube upload failed (HTTP ${httpStatus}).`;
    }
    return fallback;
  }
  const detail = String(data.message || data.error || fallback);
  if (
    data.code === 'youtube_invalid_grant' ||
    /invalid_grant/i.test(detail) ||
    /expired or revoked/i.test(String(data.error || ''))
  ) {
    return String(data.reconnectHint || YOUTUBE_RECONNECT_HINT);
  }
  if (
    data.code === 'youtube_upload_limit' ||
    isYoutubeUploadLimitError(detail)
  ) {
    return YOUTUBE_UPLOAD_LIMIT_MESSAGE;
  }
  return detail;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const snippet = text.replace(/\s+/g, ' ').trim().slice(0, 160);
    return snippet ? { message: snippet } : {};
  }
}

/**
 * POST /api/youtube/upload-binary — same contract as Upload New Breaks / Course Editor file tab.
 * @param {{
 *   fileBase64: string,
 *   title: string,
 *   description?: string,
 *   privacyStatus?: string,
 *   isShort?: boolean,
 *   account_key?: string,
 * }} options
 * @returns {Promise<{ youtubeUrl: string, uploadDate?: string, videoId?: string }>}
 */
export async function uploadYoutubeBinaryFromBase64({
  fileBase64,
  title,
  description = '',
  privacyStatus = 'unlisted',
  isShort = true,
  account_key = 'default',
}) {
  const sizeBytes = bytesFromBase64(fileBase64);
  const tooLarge = videoTooLargeMessage(sizeBytes);
  if (tooLarge) {
    throw new Error(tooLarge);
  }

  let up;
  try {
    up = await fetch(`${getApiUrl()}/api/youtube/upload-binary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileBase64,
        title,
        description,
        privacyStatus,
        isShort,
        account_key,
      }),
    });
  } catch (networkErr) {
    const msg = networkErr?.message || String(networkErr);
    if (/network|failed to fetch|load failed/i.test(msg)) {
      throw new Error(
        'Could not reach the server to upload the video. Check your connection, or try a smaller video.'
      );
    }
    throw networkErr;
  }

  const data = await parseJsonResponse(up);
  if (!up.ok) {
    throw new Error(formatYoutubeUploadError(data, 'YouTube upload failed', up.status));
  }
  const y = data.youtubeUrl;
  if (!y) throw new Error('No YouTube URL returned from server');
  return {
    youtubeUrl: String(y).trim(),
    uploadDate: data.uploadDate ? String(data.uploadDate).trim() : undefined,
    videoId: data.videoId ? String(data.videoId).trim() : undefined,
  };
}
